import React, { useState } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { SectionHeader, Callout } from '../components/UI';
import { moneyFull, pct, api } from '../lib/api';
import { Copy, Archive, ArchiveRestore, Plus, GitCompare, Check, Star, Lock, Snowflake } from 'lucide-react';
import { toast } from 'sonner';

export default function Scenarios() {
  const { scenarios, createScenario, patchScenario, deleteScenario, setActive, activeId, reloadScenarios } = useScenario();
  const [compareIds, setCompareIds] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) { toast.error('Compare up to 3 scenarios at a time.'); return prev; }
      return [...prev, id];
    });
  };

  const runCompare = async () => {
    if (compareIds.length < 2) return toast.error('Pick at least 2 scenarios.');
    setBusy(true);
    try {
      const rows = await Promise.all(compareIds.map((id) => api.get(`/scenarios/${id}/forecast`).then((r) => r.data)));
      setCompareData(rows);
    } finally { setBusy(false); }
  };

  const handleClone = async (s) => {
    const name = prompt(`Name this duplicate of "${s.name}":`, `${s.name} copy`);
    if (!name) return;
    await createScenario({ name, description: s.description, clone_from: s.id });
    toast.success(`"${name}" created — inherits every driver from "${s.name}" until you override.`);
  };

  const handleNew = async () => {
    const name = prompt('New scenario name:', 'My scenario');
    if (!name) return;
    await createScenario({ name, description: 'Custom scenario' });
    toast.success('Scenario created.');
  };

  const handleArchive = async (s) => {
    await patchScenario(s.id, { archived: !s.archived });
    toast.success(s.archived ? 'Restored.' : 'Archived.');
  };

  const handleDelete = async (s) => {
    if (!confirm(`Delete "${s.name}"? This cannot be undone.`)) return;
    try { await deleteScenario(s.id); toast.success('Deleted.'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not delete.'); }
  };

  // Batch D — keystone promotion. Only one scenario is keystone at a time
  // (backend enforces atomically); the keystone is the baseline the Budget-
  // vs-Actuals report measures drift against.
  const handleSetKeystone = async (s) => {
    if (s.keystone) return;
    await patchScenario(s.id, { keystone: true });
    await reloadScenarios();
    toast.success(`"${s.name}" is now the keystone baseline.`);
  };

  // Freeze the keystone as an immutable budget snapshot.
  const handleFreezeAsBudget = async (s) => {
    if (!confirm(`Freeze "${s.name}" as a budget snapshot?\n\nThis creates an immutable copy used by the Budget vs Actuals report. You can freeze again later to re-baseline.`)) return;
    try {
      await api.post(`/scenarios/${s.id}/freeze-as-budget`);
      await reloadScenarios();
      toast.success(`Budget snapshot created from "${s.name}".`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not freeze.');
    }
  };

  const keystone = scenarios.find((s) => s.keystone);
  const budgets = scenarios.filter((s) => s.kind === 'budget');

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Scenarios"
        title="Plan more than one future."
        description="One scenario is the KEYSTONE — the baseline every report and the Budget-vs-Actuals view measure against. Duplicate the keystone to create good/better/best variants that inherit shared assumptions. Freeze the keystone at year-start (or before a strategic pivot) to create a locked budget snapshot."
        right={
          <div className="flex gap-3">
            <button data-testid="new-scenario-btn" onClick={handleNew} className="flex items-center gap-2 bg-indie-yellow text-indie-ink font-semibold px-4 py-2.5 hover:bg-indie-yellow/85">
              <Plus size={16} /> New scenario
            </button>
            <button data-testid="compare-btn" onClick={runCompare} disabled={compareIds.length < 2 || busy} className="flex items-center gap-2 border-2 border-indie-navy text-indie-navy px-4 py-2 hover:bg-indie-navy hover:text-indie-cream disabled:opacity-50">
              <GitCompare size={16} /> Compare ({compareIds.length})
            </button>
          </div>
        }
      />

      {/* Keystone + budget summary at a glance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white border-t-4 border-indie-yellow p-5" data-testid="keystone-summary">
          <div className="flex items-center gap-2 label-editorial">
            <Star size={14} className="text-indie-yellow" /> Keystone baseline
          </div>
          <div className="mt-2 text-lg font-semibold text-indie-navy">{keystone?.name || 'None set — click a row’s star to set one'}</div>
          {keystone?.description && <div className="text-xs text-indie-ink/60 mt-1">{keystone.description}</div>}
          {keystone && (
            <button
              type="button"
              onClick={() => handleFreezeAsBudget(keystone)}
              data-testid="freeze-keystone-btn"
              className="mt-3 inline-flex items-center gap-2 border-2 border-indie-cobalt text-indie-cobalt px-3 py-1.5 text-xs uppercase tracking-wider font-semibold hover:bg-indie-cobalt hover:text-white"
            >
              <Snowflake size={13} /> Freeze as budget
            </button>
          )}
        </div>
        <div className="bg-white border-t-4 border-indie-cobalt p-5" data-testid="budgets-summary">
          <div className="flex items-center gap-2 label-editorial">
            <Lock size={14} className="text-indie-cobalt" /> Frozen budgets ({budgets.length})
          </div>
          {budgets.length === 0 ? (
            <div className="text-xs text-indie-ink/60 mt-2">
              No budget snapshots yet. Freeze the keystone above to create the baseline the Budget vs Actuals report measures against.
            </div>
          ) : (
            <ul className="mt-2 space-y-1 text-xs">
              {budgets.slice(-4).map((b) => (
                <li key={b.id} className="text-indie-ink/80">
                  <span className="font-semibold text-indie-navy">{b.name}</span>
                  {b.frozen_at && <span className="text-indie-ink/50"> · frozen {new Date(b.frozen_at).toLocaleDateString()}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="bg-white border-t-4 border-indie-navy overflow-x-auto">
        <table data-testid="scenarios-table" className="w-full text-sm">
          <thead>
            <tr className="bg-indie-navy text-indie-cream">
              <th className="text-left px-4 py-3 font-medium tracking-wider uppercase text-xs w-8"></th>
              <th className="text-left px-4 py-3 font-medium tracking-wider uppercase text-xs">Name</th>
              <th className="text-left px-4 py-3 font-medium tracking-wider uppercase text-xs">Kind</th>
              <th className="text-left px-4 py-3 font-medium tracking-wider uppercase text-xs">Description</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">Versions</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">Actions</th>
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s, i) => {
              const selected = compareIds.includes(s.id);
              const isBudget = s.kind === 'budget';
              return (
                <tr key={s.id} className={`${i % 2 === 0 ? 'bg-white' : 'bg-indie-cream'} ${s.archived ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleCompare(s.id)} className={`h-5 w-5 border-2 flex items-center justify-center ${selected ? 'bg-indie-yellow border-indie-navy' : 'border-indie-navy/40'}`}>
                      {selected && <Check size={12} className="text-indie-ink" />}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-indie-navy flex items-center gap-2 flex-wrap">
                      {s.name}
                      {s.keystone && (
                        <span data-testid={`keystone-badge-${s.id}`} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-indie-yellow text-indie-ink uppercase tracking-wider font-semibold">
                          <Star size={9} /> Keystone
                        </span>
                      )}
                      {isBudget && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-indie-cobalt/15 text-indie-cobalt uppercase tracking-wider font-semibold">
                          <Lock size={9} /> Frozen budget
                        </span>
                      )}
                      {s.id === activeId && <span className="text-[10px] px-1.5 py-0.5 bg-indie-navy text-indie-cream uppercase tracking-wider font-semibold">Active</span>}
                      {s.parent_scenario_id && !isBudget && (
                        <span className="text-[10px] text-indie-ink/50" title="Duplicated from a parent — shared assumptions inherit until overridden">
                          ↳ inherits
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize text-indie-cobalt">{s.kind || 'forecast'}</td>
                  <td className="px-4 py-3 text-indie-ink/80 max-w-md">{s.description}</td>
                  <td className="px-4 py-3 text-right num-tabular text-indie-ink/60">{(s.versions || []).length}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      {!isBudget && !s.keystone && (
                        <button
                          onClick={() => handleSetKeystone(s)}
                          data-testid={`set-keystone-${s.id}`}
                          className="text-indie-ink/50 hover:text-indie-yellow"
                          title="Set as keystone baseline"
                        >
                          <Star size={15} />
                        </button>
                      )}
                      <button onClick={() => setActive(s.id)} className="text-xs uppercase tracking-wider text-indie-cobalt hover:text-indie-navy px-2" disabled={s.archived}>Use</button>
                      <button onClick={() => handleClone(s)} className="text-indie-navy/70 hover:text-indie-navy" title="Duplicate — new scenario inherits shared assumptions"><Copy size={15} /></button>
                      {!isBudget && (
                        <button onClick={() => handleArchive(s)} className="text-indie-navy/70 hover:text-indie-navy" title={s.archived ? 'Restore' : 'Archive'}>
                          {s.archived ? <ArchiveRestore size={15} /> : <Archive size={15} />}
                        </button>
                      )}
                      {!s.is_default && (
                        <button onClick={() => handleDelete(s)} className="text-red-600 hover:text-red-800 text-xs uppercase tracking-wider">Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {compareData && (
        <div data-testid="compare-panel" className="bg-white border-t-4 border-indie-evergreen p-6">
          <div className="label-editorial mb-3">Side-by-side</div>
          <table className="w-full text-sm num-tabular">
            <thead>
              <tr className="bg-indie-navy text-indie-cream">
                <th className="text-left px-3 py-2 uppercase tracking-wider text-xs">Metric</th>
                {compareData.map((c) => (
                  <th key={c.scenario.id} className="text-right px-3 py-2 uppercase tracking-wider text-xs">{c.scenario.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['FY Revenue', (c) => moneyFull(c.forecast.totals.revenue)],
                ['FY Net Income', (c) => moneyFull(c.forecast.totals.net_income)],
                ['Margin', (c) => pct(c.forecast.totals.margin)],
                ['Ending Cash', (c) => moneyFull(c.forecast.totals.ending_cash)],
              ].map(([label, fn], i) => (
                <tr key={label} className={i % 2 === 0 ? 'bg-white' : 'bg-indie-cream'}>
                  <td className="px-3 py-2.5 font-medium">{label}</td>
                  {compareData.map((c) => <td key={c.scenario.id} className="text-right px-3 py-2.5">{fn(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Callout title="How scenarios and budgets work together">
        <p className="mb-2">
          <strong>Keystone</strong> = the single active baseline. Every P&L, Cash Flow, Dashboard and BHAG number ties back to it. Only one at a time.
        </p>
        <p className="mb-2">
          <strong>Duplicate</strong> → the new scenario inherits everything from the keystone until you override a specific driver. Change a shared static cost on the keystone and it flows through.
        </p>
        <p>
          <strong>Freeze as budget</strong> → an immutable snapshot of the keystone. This is what the Budget vs Actuals report measures QuickBooks drift against. Re-freeze whenever you re-baseline (e.g., mid-year plan reset).
        </p>
      </Callout>
    </div>
  );
}
